import * as React from 'react';
import { cn } from '#root/lib/utils';
import styles from './card.module.scss';

/**
 * Card Component - Design System
 *
 * Contenedor base para cards. Reemplaza GuveryPaper con API moderna.
 * Soporta composition pattern con subcomponentes CardHeader, CardTitle, etc.
 *
 * @example
 * // Card básico
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Título</CardTitle>
 *   </CardHeader>
 *   <CardContent>
 *     Contenido del card
 *   </CardContent>
 * </Card>
 *
 * @example
 * // Card con footer
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Pedido #123</CardTitle>
 *     <CardDescription>Hace 2 horas</CardDescription>
 *   </CardHeader>
 *   <CardContent>
 *     Detalles del pedido...
 *   </CardContent>
 *   <CardFooter>
 *     <Button>Hacer Oferta</Button>
 *   </CardFooter>
 * </Card>
 */

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Si true, remueve el shadow/elevation
   */
  flat?: boolean;
  /**
   * Si true, remueve el borde
   */
  noBorder?: boolean;
  /**
   * Si true, deshabilita el efecto hover
   */
  noHover?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, flat, noBorder, noHover, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        styles.card,
        flat && styles.flat,
        noBorder && styles.noBorder,
        noHover && styles.noHover,
        className
      )}
      {...props}
    />
  )
);
Card.displayName = 'Card';

/**
 * CardHeader - Header section del card
 */
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn(styles.header, className)} {...props} />
  )
);
CardHeader.displayName = 'CardHeader';

/**
 * CardTitle - Título principal del card
 */
export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /**
   * Nivel del heading (h1-h6)
   * @default "h3"
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Component = 'h3', ...props }, ref) => (
    <Component ref={ref} className={cn(styles.title, className)} {...props} />
  )
);
CardTitle.displayName = 'CardTitle';

/**
 * CardDescription - Subtítulo o descripción del card
 */
export interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

const CardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn(styles.description, className)} {...props} />
  )
);
CardDescription.displayName = 'CardDescription';

/**
 * CardContent - Contenido principal del card
 */
export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn(styles.content, className)} {...props} />
  )
);
CardContent.displayName = 'CardContent';

/**
 * CardFooter - Footer section del card (acciones, botones)
 */
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn(styles.footer, className)} {...props} />
  )
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
